'use client';

import { usePathname } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { ContactUsLink } from '@/lib/components/ContactUsLink';
import { Logo } from '@/lib/components/Logo';
import { LEADERBOARD_PATH } from '@/lib/components/RewardsHub/routes';
import { FEATURE_FLAGS } from '@/lib/config/features';
import { DOCS_URL } from '@/lib/constants/docs';
import { useGooglePlayUrl } from '@/lib/hooks/useGooglePlayUrl';
import { Link } from '@/lib/routing';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';

const SOCIAL_LINKS = [
  {
    icon: 'Twitter' as const,
    socialKey: 'twitter' as const,
    href: 'https://twitter.com/outpoll_com?buttonType=FollowButton',
  },
  {
    icon: 'Youtube' as const,
    socialKey: 'youtube' as const,
    href: 'https://www.youtube.com/@outpoll-com?sub_confirmation=1',
  },
  {
    icon: 'Telegram' as const,
    socialKey: 'telegram' as const,
    href: 'https://t.me/outpoll_com',
  },
];

const SUPPORT_EMAIL = 'support@outpoll.com';

const FEEDBACK_FORM_URL = 'https://forms.clickup.com/9014872600/f/8cn82gr-4434/B4SN0TUK2HIPUQYXNG';

const linkCls =
  'text-[13px] font-normal leading-[16px] tracking-[-0.13px] text-v2-text-primary transition-opacity hover:opacity-60 whitespace-nowrap';

const sectionHeadingCls = 'text-[12px] font-normal leading-[1.15] text-v2-text-secondary';

interface FooterV2Props {
  className?: string;
}

export function FooterV2({ className }: FooterV2Props) {
  const googlePlayUrl = useGooglePlayUrl();
  const t = useTranslations('footer');
  const tNav = useTranslations('nav');
  const year = new Date().getFullYear();
  const pathname = usePathname();
  const locale = useLocale();
  // Zone-aware Explore links: cross-zone targets must be plain <a>.
  const isInNewsZone = pathname?.includes('/news') ?? false;

  const exploreLinks = [
    {
      label: tNav('markets'),
      href: isInNewsZone ? `/${locale}` : '/',
      external: isInNewsZone,
    },
    // The trade zone, pre-production only for now (mirrors HeaderV2Container).
    ...(FEATURE_FLAGS.ENABLE_TRADE_ZONE
      ? [
          {
            label: tNav('cryptoTrading'),
            href: `/${locale}/trade`,
            external: true,
          },
        ]
      : []),
    {
      label: tNav('news'),
      href: isInNewsZone ? '/news' : `/${locale}/news`,
      external: !isInNewsZone,
    },
    {
      label: tNav('leaderboard'),
      href: isInNewsZone ? `/${locale}${LEADERBOARD_PATH}` : LEADERBOARD_PATH,
      external: isInNewsZone,
    },
  ];

  const exploreExternal = [{ label: tNav('api'), href: DOCS_URL.apiIntroduction }];

  const legalLinks = [
    { label: t('termsOfService'), href: DOCS_URL.termsOfService },
    { label: t('privacyPolicy'), href: DOCS_URL.privacyPolicy },
    { label: t('cookiePolicy'), href: DOCS_URL.cookiePolicy },
  ];

  return (
    <footer className={cn('relative w-full overflow-hidden', className)}>
      {/* Blue halo from below — matches Figma `bg down` (node 268:10561) */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 1440 800"
        preserveAspectRatio="none"
      >
        <title>Footer background glow</title>
        <g filter="url(#footer-blur)">
          <ellipse
            cx="720"
            cy="1260.31"
            rx="767.448"
            ry="797.25"
            transform="rotate(90 720 1260.31)"
            fill="#0B71FF"
            fillOpacity="0.1"
          />
        </g>
        <defs>
          <filter
            id="footer-blur"
            x="-377.25"
            y="192.865"
            width="2194.5"
            height="2134.9"
            filterUnits="userSpaceOnUse"
          >
            <feGaussianBlur stdDeviation="150" />
          </filter>
        </defs>
      </svg>
      <div className="relative mx-auto flex w-full max-w-[1464px] flex-col gap-8 px-4 pt-6 pb-[150px] md:px-[60px] min-[1024px]:pt-[60px] min-[1024px]:pb-[60px]">
        {/* Logo + tagline. The data-back-to-top-anchor row is the resting
            spot for the floating Back-to-top button — it rides up and locks
            its vertical center to this row's center as the footer enters view. */}
        <div className="flex flex-col gap-3">
          <div data-back-to-top-anchor>
            <Logo />
          </div>
          <p className="text-[13px] font-normal leading-[1.5] text-v2-text-primary">
            {t('tagline')}
          </p>
        </div>

        {/* Navigation columns */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:flex sm:flex-row sm:justify-between sm:gap-x-0">
          {/* Explore */}
          <div className="flex flex-1 flex-col gap-4">
            <p className={sectionHeadingCls}>{t('sections.explore')}</p>
            <div className="flex flex-col gap-4">
              {exploreLinks.map(({ label, href, external }) =>
                external ? (
                  <a key={label} href={href} className={linkCls}>
                    {label}
                  </a>
                ) : (
                  <Link key={label} href={href} className={linkCls}>
                    {label}
                  </Link>
                ),
              )}
              {exploreExternal.map(({ label, href }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" className={linkCls}>
                  {label}
                </a>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div className="flex flex-1 flex-col gap-4">
            <p className={sectionHeadingCls}>{t('sections.legal')}</p>
            <div className="flex flex-col gap-4">
              {legalLinks.map(({ label, href }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" className={linkCls}>
                  {label}
                </a>
              ))}
            </div>
          </div>

          {/* Support & Social */}
          <div className="flex flex-1 flex-col gap-16">
            {/* Support links */}
            <div className="flex flex-col gap-4">
              <p className={sectionHeadingCls}>{t('sections.supportSocial')}</p>
              <div className="flex flex-col gap-4">
                <a href={DOCS_URL.home} target="_blank" rel="noreferrer" className={linkCls}>
                  {t('helpCenter')}
                </a>
                <ContactUsLink email={SUPPORT_EMAIL} label={t('contactUs')} className={linkCls} />
                <a href={FEEDBACK_FORM_URL} target="_blank" rel="noreferrer" className={linkCls}>
                  {t('feedbackForm')}
                </a>
              </div>
            </div>

            <div className="flex flex-col gap-10">
              {/* Follow Us */}
              <div className="flex flex-col gap-2">
                <p className={linkCls}>{t('followUsTitle')}</p>
                <div className="flex items-center gap-2">
                  {SOCIAL_LINKS.map(({ icon, socialKey, href }) => (
                    <a
                      key={icon}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={t(`social.${socialKey}`)}
                      className="flex size-9 items-center justify-center rounded-full bg-v2-bg-blue-5 p-1.5 text-v2-icon-primary transition-colors hover:bg-v2-bg-blue-10 hover:text-v2-icon-primary-hover"
                    >
                      <Icon size={24} name={icon} />
                    </a>
                  ))}
                </div>
              </div>

              {/* Download App */}
              <div className="flex flex-col gap-2">
                <p className={linkCls}>{tNav('downloadApp')}</p>
                <a
                  href={googlePlayUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t('getItOnGooglePlay')}
                  className="flex w-fit items-center gap-2 py-2 transition-opacity hover:opacity-60"
                >
                  <Icon name="GooglePlay" size={16} className="text-v2-text-secondary" />
                  <span className="text-[12px] font-normal leading-[16px] tracking-[-0.12px] text-v2-text-primary">
                    {t('getItOnGooglePlay')}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <p className={sectionHeadingCls}>{t('copyright', { year })}</p>
      </div>
    </footer>
  );
}
